import { useParams } from 'react-router';
import { Header } from '../components/Header';
import { Chip, EmptyState, ErrorState, Spinner, formatDate } from '../components/ui';
import { useAudit, useDocument } from '../api/queries';
import type { ResolvedAuditEvent } from '../api/endpoints';
import { usePeople, type NameOf } from '../model/people';
import { useServerEvents } from '../api/events';

function pretty(v: unknown): string {
  if (v === undefined) return '';
  if (v === null) return 'null';
  return typeof v === 'string' ? v : JSON.stringify(v, null, 2);
}

function Row({ e, nameOf }: { e: ResolvedAuditEvent; nameOf: NameOf }) {
  const hasDiff = e.before !== undefined || e.after !== undefined;
  return (
    <tr>
      <td className="mono muted" style={{ whiteSpace: 'nowrap' }}>{formatDate(e.ts)}</td>
      <td>{nameOf(e.actor, e.actorId)}<div className="muted small">{e.actor ? `${e.actor.role} via ${e.actorType}` : e.actorType}</div></td>
      <td><Chip>{e.action}</Chip></td>
      <td><div>{e.entity}</div><div className="mono muted">{e.entityId}</div></td>
      <td>
        {hasDiff ? (
          <details>
            <summary>Before / after</summary>
            <div className="diff2">
              <pre>{pretty(e.before) || '(none)'}</pre>
              <pre>{pretty(e.after) || '(none)'}</pre>
            </div>
          </details>
        ) : <span className="muted">No payload</span>}
      </td>
    </tr>
  );
}

export function Audit() {
  const { documentId = '' } = useParams();
  const doc = useDocument(documentId);
  const q = useAudit(documentId);
  const { nameOf } = usePeople();
  useServerEvents(() => { void q.refetch(); });
  return (
    <div className="app">
      <Header crumbs={[{ label: 'Projects', to: '/projects' }, { label: doc.data?.name ?? 'Document', to: `/documents/${documentId}` }, { label: 'Audit' }]} />
      <main className="main">
        <div className="page audit" style={{ maxWidth: 1200 }}>
          <div className="page-head"><h1>Audit log</h1><span className="sub">Every change in either direction, newest first</span></div>
          {q.isPending && <Spinner label="Loading events" />}
          {q.isError && <ErrorState title="Could not load the audit log" error={q.error} retry={() => void q.refetch()} />}
          {q.data && q.data.length === 0 && <EmptyState title="Nothing recorded yet">Edits, comments, decisions and publishes will show up here.</EmptyState>}
          {q.data && q.data.length > 0 && (
            <div className="card" style={{ overflow: 'auto' }}>
              <table>
                <thead><tr><th>Time</th><th>Actor</th><th>Action</th><th>Entity</th><th>Detail</th></tr></thead>
                <tbody>{q.data.map((e) => <Row key={e.id} e={e} nameOf={nameOf} />)}</tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
